import { RenderTemplateCallback, SignatureProps, SingleHeadlineProps } from "../../src/types";
import { colors, TemplateKeys, backgrounds, watermarks, content } from "./templates/shared";

export const renderTemplate: RenderTemplateCallback = async (ctx, utils, template, data, signatures) => await renderTest(ctx, utils, template, data, signatures); 

const renderHeadline = (text: string, name: string, props: SingleHeadlineProps, text2?: string) => {
    const font = props.utils.fontScaler(props.template);
    const scale = props.utils.templateScaler(props.template);
    props.ctx.font = font(props.template.font[name].size, props.template.font[name].family);
    let headlineText = text;


    if (text2) {
        headlineText = `${text}\n${text2}`;
        const longestLine = headlineText.split('\n').reduce((longest, line) => {
            if (line.length > longest.length) {
                return line;
            }
            return longest;
        }, '');
    }

    const centerOffset = props.textLeftMargin;
    let headMargin = 0;

    if (props.template.font[name].headMarginLeft) {
        headMargin = props.template.font[name].headMarginLeft;
    }

    if (props.template.font[name].headMarginRight) {
        headMargin = props.template.font[name].headMarginRight;
    }

    props.utils.renderText(props.ctx, {
        font: props.ctx.font,
        text: headlineText,
        fillStyle: props.template.font[name].color,
        x: centerOffset + headMargin,
        y: name === 'subHeadline' ? props.template.font.content.TopPadding + Math.max(scale(props.template.font.subHeadline.TopPadding), props.contentHeight + scale(props.template.font.subHeadline.yScale2)) : scale(props.template.font[name].TopPadding),
        lineHeight: scale(props.template.font[name].lineHeight) ? scale(props.template.font[name].lineHeight) : undefined,
        centered: props.contentCentered
    });
}


export const renderSignature = (signatures, props: SignatureProps) => {
    const font = props.utils.fontScaler(props.template);
    const scale = props.utils.templateScaler(props.template);
    let left = props.textLeftMargin - props.contentWidth / 2;
    let top = props.template.signature.topPadding + (props.template.signature.contentDep ? Math.max(scale(60), props.contentHeight + scale(60)) : 0);
    const singleSignature = props.template.signature.singleSignature;

    for (const signature of signatures) {
        props.ctx.save();
        const m = props.ctx.measureText(signature.signature);
        let willBreak = false;
        if (left + m.width + scale(60)  > props.contentWidth + props.textLeftMargin / 2) {
            willBreak = true;
        }
        props.ctx.restore();
        const measure = props.utils.renderSignature(props.ctx, {
            signature: {
                font: font(props.template.signature.fontSize, 'AlFresco'),
                text: signature.signature,
                fillStyle: props.template.signature.color
            },
            subtitle: {
                font: font(props.template.signature.fontSizeSub, 'Poppins-SemiBold'),
                text: signature.subtitle,
                fillStyle: props.template.signature.color,
                offset: scale(25)
            },
            centered: true,

            x: singleSignature ? props.textLeftMargin - m.width : willBreak ? props.textLeftMargin - (props.contentWidth / 2) : left,
            y: top + (willBreak ? scale(80) : 0)
        })
        left += measure.width + scale(20);
        if (left + scale(50) > props.textLeftMargin + props.contentWidth) {
            top += measure.height + scale(80);
            left = props.textLeftMargin - (props.contentWidth / 2) + (willBreak ? measure.width + scale(20) : 0);
        }
    }
}

export const renderTest: RenderTemplateCallback = async (ctx, utils, template, data, signatures) => {

    const templateKey = template.key as TemplateKeys;
    const backgroundImg = await utils.getImage(backgrounds[templateKey]);
    const watermarkImg = await utils.getImage(watermarks[templateKey]);
    const checkmarkImg = templateKey === 'boardCard' ? await utils.getImage(require('./assets/images/checkmark.png').default) : null;

    const scale = utils.templateScaler(template);
    const font = utils.fontScaler(template);
    const image = utils.imageScaler(template);

    const width = scale(1, '%w');
    const height = scale(1, '%h');

    ctx.clearRect(0, 0, width, height);

    ctx.fillStyle = colors[templateKey].background;
    ctx.fillRect(0, 0, width, height);

    image(ctx, {
        image: backgroundImg,
        left: 0,
        top: 0,
        scale: templateKey === 'boardCard' ? 1 : 0.5
    });

    if(templateKey !== 'boardCard') {
        image(ctx, {
            image: watermarkImg,
            left: 0,
            top: 0,
            scale: 0.5
        });
    }




    let contentTopPadding = 0;
    const contentCentered = content[templateKey].centered;
    const contentWidth = scale(content[templateKey].width);
    const textLeftMargin = scale(content[templateKey].left);

    ctx.save();

    // Skip default content rendering for boardCard (has custom rendering)
    let contentHeight = 0;
    if (templateKey !== 'boardCard') {
        contentTopPadding = template.font.content && template.font.content.TopPadding;
        const result = utils.renderText(ctx, {
            font: font(template.font.content.size, template.font.content.family),
            text: utils.getText(data.content).trim(),
            fillStyle: template.font.content.color,
            x: textLeftMargin,
            y: contentTopPadding,
            lineHeight: scale(template.font.content.lineHeight),
            maxWidth: contentWidth,
            centered: contentCentered
        });
        contentHeight = result.height;
    }


    

    const headlineTop = template.font.headlineTop && utils.getText(data.headlineTop).trim();
    const headlineBottom = template.font.headlineBottom && utils.getText(data.headlineBottom).trim();
    const subHeadline = template.font.subHeadline && utils.getText(data.subHeadline).trim();
    const subHeadline1 = template.font.subHeadline1 && utils.getText(data.subHeadline1).trim();
    const subHeadline2 = template.font.subHeadline2 && utils.getText(data.subHeadline2).trim();
    const subHeadline3 = template.font.subHeadline3 && utils.getText(data.subHeadline3).trim();
    const subHeadline4 = template.font.subHeadline4 && utils.getText(data.subHeadline4).trim();

    const props: SingleHeadlineProps = {
        template,
        ctx,
        utils,
        textLeftMargin,
        contentCentered,
        contentWidth,
        templateKey,
        contentHeight
    }

    headlineTop && headlineBottom && template.font.doubleHeadline ? renderHeadline(headlineTop, 'headlineTop', props, headlineBottom) : undefined;
    headlineTop && !template.font.doubleHeadline ? renderHeadline(headlineTop, 'headlineTop', props) : undefined;
    headlineBottom && !template.font.doubleHeadline ? renderHeadline(headlineBottom, 'headlineBottom', props) : undefined;
    subHeadline && renderHeadline(subHeadline, 'subHeadline', props);
    subHeadline1 && renderHeadline(subHeadline1, 'subHeadline1', props);
    subHeadline2 && renderHeadline(subHeadline2, 'subHeadline2', props);
    subHeadline3 && !template.font.doubleSubHeadline ? renderHeadline(subHeadline3, 'subHeadline3', props) : undefined;
    subHeadline4 && !template.font.doubleSubHeadline ? renderHeadline(subHeadline4, 'subHeadline4', props) : undefined;
    subHeadline3 && subHeadline4 && template.font.doubleSubHeadline ? renderHeadline(subHeadline3, 'subHeadline3', props, subHeadline4) : undefined;

    ctx.restore();

    // Board Card specific rendering
    if (templateKey === 'boardCard' && checkmarkImg) {
        const sucheOderBiete = utils.getText(data.sucheOderBiete);
        const description = utils.getText(data.description);
        const name = utils.getText(data.name);
        const strasse = utils.getText(data.strasse);
        const plzOrt = utils.getText(data.plzOrt);
        const tel = utils.getText(data.tel);
        const email = utils.getText(data.email);

        if (sucheOderBiete === 'Suche') {
            image(ctx, {
                image: checkmarkImg,
                left: 460,
                top: 200,
                scale: 0.2
            });
        } else if (sucheOderBiete === 'Biete') {
            image(ctx, {
                image: checkmarkImg,
                left: 800,
                top: 200,
                scale: 0.2
            });
        }

        // Render description (2 lines after Suche/Biete)
        ctx.font = font(35, 'Poppins-Regular');
        ctx.fillStyle = '#000';
        utils.renderText(ctx, {
            font: ctx.font,
            text: description,
            fillStyle: '#000',
            x: scale(170),
            y: scale(350),
            lineHeight: scale(80),
            maxWidth: scale(800),
            centered: false
        });

        // Render form fields
        const formFieldFont = font(35, 'Poppins-Regular');
        const formFieldColor = '#000';

        // Name
        utils.renderText(ctx, {
            font: formFieldFont,
            text: name,
            fillStyle: formFieldColor,
            x: scale(310),
            y: scale(540),
            centered: false
        });

        // Straße
        utils.renderText(ctx, {
            font: formFieldFont,
            text: strasse,
            fillStyle: formFieldColor,
            x: scale(310),
            y: scale(615),
            centered: false
        });

        // PLZ / Ort
        utils.renderText(ctx, {
            font: formFieldFont,
            text: plzOrt,
            fillStyle: formFieldColor,
            x: scale(360),
            y: scale(695),
            centered: false
        });

        // Tel
        utils.renderText(ctx, {
            font: formFieldFont,
            text: tel,
            fillStyle: formFieldColor,
            x: scale(250),
            y: scale(775),
            centered: false
        });

        // E-Mail
        utils.renderText(ctx, {
            font: formFieldFont,
            text: email,
            fillStyle: formFieldColor,
            x: scale(310),
            y: scale(855),
            centered: false
        });
    }

    template.signatureInfo && renderSignature(signatures, props);
}
